import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { MapPin, Phone, Mail, Globe, ArrowLeft, Building2, Clock, User, Star, Send, MessageCircle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from '../components/ui/dialog';
import { toast } from 'sonner';
import axios from 'axios';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import AuthGate from '../components/AuthGate';
import { useAuth, API } from '../App';

export default function ProviderProfilePage() {
  const { providerId } = useParams();
  const { token, user } = useAuth();
  const [provider, setProvider] = useState(null);
  const [loading, setLoading] = useState(true);
  const [inquiryOpen, setInquiryOpen] = useState(false);
  const [messageOpen, setMessageOpen] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messageText, setMessageText] = useState('');
  const [reviews, setReviews] = useState([]);
  const [reviewStats, setReviewStats] = useState({ average_rating: 0, total_count: 0 });
  const [userHasReviewed, setUserHasReviewed] = useState(false);
  const [reviewForm, setReviewForm] = useState({ rating: 5, comment: '' });
  const [inquiryForm, setInquiryForm] = useState({
    name: '',
    email: '',
    phone: '',
    message: ''
  });

  useEffect(() => {
    fetchProvider();
    fetchReviews();
  }, [providerId]);

  useEffect(() => {
    if (user) {
      setInquiryForm(prev => ({
        ...prev,
        name: user.name || '',
        email: user.email || ''
      }));
    }
  }, [user]);

  const fetchProvider = async () => {
    try {
      const response = await axios.get(`${API}/providers/${providerId}`);
      setProvider(response.data);
    } catch (error) {
      toast.error('Provider not found');
    } finally {
      setLoading(false);
    }
  };

  const fetchReviews = async () => {
    try {
      const response = await axios.get(`${API}/providers/${providerId}/reviews`);
      setReviews(response.data.reviews || []);
      setReviewStats({
        average_rating: response.data.average_rating || 0,
        total_count: response.data.total_count || 0
      });
      
      // Check if current user has already reviewed
      if (user) {
        const userReview = response.data.reviews?.find(r => r.user_id === user.user_id);
        setUserHasReviewed(!!userReview);
      }
    } catch (error) {
      // Silently fail
    }
  };

  const handleSubmitReview = async (e) => {
    e.preventDefault();
    if (!token) {
      toast.error('Please login to leave a review');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API}/providers/${providerId}/reviews`, reviewForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Review submitted successfully!');
      setReviewOpen(false);
      setReviewForm({ rating: 5, comment: '' });
      fetchReviews();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const StarRating = ({ rating, onRatingChange, interactive = false, size = 'md' }) => {
    const sizeClasses = size === 'sm' ? 'h-4 w-4' : 'h-6 w-6';
    return (
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type={interactive ? 'button' : undefined}
            onClick={interactive ? () => onRatingChange(star) : undefined}
            disabled={!interactive}
            className={interactive ? 'cursor-pointer' : 'cursor-default'}
          >
            <Star
              className={`${sizeClasses} ${star <= rating ? 'fill-amber-400 text-amber-400' : 'text-slate-300'}`}
            />
          </button>
        ))}
      </div>
    );
  };

  const handleInquirySubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await axios.post(`${API}/inquiries`, {
        target_type: 'provider',
        target_id: providerId,
        ...inquiryForm
      }, token ? { headers: { Authorization: `Bearer ${token}` } } : {});
      
      toast.success('Message sent! The provider will contact you soon.');
      setInquiryOpen(false);
      setInquiryForm({ name: user?.name || '', email: user?.email || '', phone: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendMessage = async () => {
    if (!token) {
      toast.error('Please login to send messages');
      return;
    }
    if (!messageText.trim()) {
      toast.error('Please enter a message');
      return;
    }

    setSendingMessage(true);
    try {
      await axios.post(`${API}/conversations`, {
        recipient_id: provider.user_id,
        provider_id: providerId,
        message: messageText,
        message_type: 'message'
      }, { headers: { Authorization: `Bearer ${token}` } });

      toast.success('Message sent! Check your Messages tab for replies.');
      setMessageOpen(false);
      setMessageText('');
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-12">
          <div className="animate-pulse space-y-6">
            <div className="h-6 bg-slate-200 rounded w-1/4" />
            <div className="bg-white rounded-xl p-8">
              <div className="flex items-start gap-6 mb-6">
                <div className="w-24 h-24 bg-slate-200 rounded-xl" />
                <div className="flex-1 space-y-3">
                  <div className="h-4 bg-slate-200 rounded w-1/4" />
                  <div className="h-8 bg-slate-200 rounded w-1/2" />
                  <div className="h-4 bg-slate-200 rounded w-1/3" />
                </div>
              </div>
              <div className="h-32 bg-slate-100 rounded" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!provider) {
    return (
      <div className="min-h-screen bg-slate-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-6 py-12 text-center">
          <h1 className="font-heading text-2xl font-bold text-slate-900 mb-4">Provider Not Found</h1>
          <p className="text-slate-600 mb-6">This provider may have been removed or is no longer active.</p>
          <Button asChild>
            <Link to="/directory">Browse Directory</Link>
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <AuthGate title="Sign Up Free to View Provider Profiles">
      <div className="max-w-4xl mx-auto px-6 py-8">
        {/* Back Button */}
        <Link to="/directory" className="inline-flex items-center text-slate-600 hover:text-slate-900 mb-6" data-testid="back-link">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Directory
        </Link>

        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          {/* Header */}
          <div className="p-8 border-b border-slate-200">
            <div className="flex flex-col md:flex-row md:items-start gap-6">
              <div className="w-24 h-24 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                {provider.logo_url ? (
                  <img src={provider.logo_url} alt={provider.business_name} className="w-full h-full object-cover rounded-xl" />
                ) : (
                  <Building2 className="h-12 w-12 text-blue-600" />
                )}
              </div>
              
              <div className="flex-1">
                <span className="overline text-blue-600 mb-2 block">{provider.category}</span>
                <h1 className="font-heading text-3xl font-bold text-slate-900 mb-2" data-testid="provider-name">
                  {provider.business_name}
                </h1>
                <div className="flex items-center gap-4 text-slate-600 mb-2">
                  <div className="flex items-center gap-1">
                    <MapPin className="h-5 w-5" />
                    <span>{provider.service_area}</span>
                  </div>
                </div>
                {/* Rating Display */}
                <div className="flex items-center gap-2" data-testid="provider-rating">
                  <StarRating rating={Math.round(reviewStats.average_rating)} size="sm" />
                  <span className="text-slate-700 font-medium">{reviewStats.average_rating.toFixed(1)}</span>
                  <span className="text-slate-500">({reviewStats.total_count} review{reviewStats.total_count !== 1 ? 's' : ''})</span>
                </div>
              </div>

              <div className="flex flex-col gap-3 w-full md:w-auto">
                {/* Direct Message Button */}
                <Dialog open={messageOpen} onOpenChange={setMessageOpen}>
                  <DialogTrigger asChild>
                    <Button className="bg-[#5DADE2] hover:bg-[#4A9BD1] rounded-full" size="lg" data-testid="message-provider-btn">
                      <MessageCircle className="h-5 w-5 mr-2" />
                      Send Message
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Message {provider.business_name}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="dm-message">Your Message</Label>
                        <Textarea
                          id="dm-message"
                          required
                          rows={4}
                          value={messageText}
                          onChange={(e) => setMessageText(e.target.value)}
                          placeholder="Hi, I'm interested in your services..."
                          className="mt-1"
                        />
                      </div>
                      <Button 
                        onClick={handleSendMessage} 
                        disabled={sendingMessage || !messageText.trim()} 
                        className="w-full bg-[#5DADE2] hover:bg-[#4A9BD1] rounded-full"
                      >
                        {sendingMessage ? 'Sending...' : 'Send Message'}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>

                {/* Legacy Contact Form */}
                <Dialog open={inquiryOpen} onOpenChange={setInquiryOpen}>
                  <DialogTrigger asChild>
                    <Button variant="outline" className="rounded-full" size="lg" data-testid="contact-provider-btn">
                      <Mail className="h-5 w-5 mr-2" />
                      Contact via Email
                    </Button>
                  </DialogTrigger>
                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>Contact {provider.business_name}</DialogTitle>
                    </DialogHeader>
                    <form onSubmit={handleInquirySubmit} className="space-y-4 mt-4">
                      <div>
                        <Label htmlFor="inq-name">Name</Label>
                        <Input
                          id="inq-name"
                          required
                          value={inquiryForm.name}
                          onChange={(e) => setInquiryForm(prev => ({ ...prev, name: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="inq-email">Email</Label>
                        <Input
                          id="inq-email"
                          type="email"
                          required
                          value={inquiryForm.email}
                          onChange={(e) => setInquiryForm(prev => ({ ...prev, email: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="inq-phone">Phone (optional)</Label>
                        <Input
                          id="inq-phone"
                          value={inquiryForm.phone}
                          onChange={(e) => setInquiryForm(prev => ({ ...prev, phone: e.target.value }))}
                          className="mt-1"
                        />
                      </div>
                      <div>
                        <Label htmlFor="inq-message">Message</Label>
                        <Textarea
                          id="inq-message"
                          required
                          rows={4}
                          value={inquiryForm.message}
                          onChange={(e) => setInquiryForm(prev => ({ ...prev, message: e.target.value }))}
                          placeholder="I'm interested in learning more about your services..."
                          className="mt-1"
                        />
                      </div>
                      <Button type="submit" disabled={submitting} className="w-full bg-blue-700 hover:bg-blue-800 rounded-full">
                        {submitting ? 'Sending...' : 'Send Message'}
                      </Button>
                    </form>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </div>

          {/* Content */}
          <div className="p-8">
            {/* About */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">About</h2>
              <p className="text-slate-600 leading-relaxed whitespace-pre-wrap">
                {provider.bio}
              </p>
            </div>

            {/* Contact Info */}
            <div className="mb-8">
              <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">Contact Information</h2>
              <div className="bg-slate-50 rounded-xl p-6 space-y-4">
                {provider.phone && (
                  <a href={`tel:${provider.phone}`} className="flex items-center gap-3 text-slate-700 hover:text-blue-700 transition-colors">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-slate-200">
                      <Phone className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Phone</div>
                      <div className="font-medium">{provider.phone}</div>
                    </div>
                  </a>
                )}
                {provider.email && (
                  <a href={`mailto:${provider.email}`} className="flex items-center gap-3 text-slate-700 hover:text-blue-700 transition-colors">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-slate-200">
                      <Mail className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Email</div>
                      <div className="font-medium">{provider.email}</div>
                    </div>
                  </a>
                )}
                {provider.website && (
                  <a href={provider.website} target="_blank" rel="noopener noreferrer" className="flex items-center gap-3 text-slate-700 hover:text-blue-700 transition-colors">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-slate-200">
                      <Globe className="h-5 w-5" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Website</div>
                      <div className="font-medium">{provider.website}</div>
                    </div>
                  </a>
                )}
              </div>
            </div>

            {/* Service Details */}
            <div>
              <h2 className="font-heading text-xl font-semibold text-slate-900 mb-4">Service Details</h2>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-slate-200">
                      <Building2 className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Category</div>
                      <div className="font-medium text-slate-900">{provider.category}</div>
                    </div>
                  </div>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-slate-200">
                      <MapPin className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Service Area</div>
                      <div className="font-medium text-slate-900">{provider.service_area}</div>
                    </div>
                  </div>
                </div>
                <div className="bg-slate-50 rounded-xl p-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center border border-slate-200">
                      <Clock className="h-5 w-5 text-slate-600" />
                    </div>
                    <div>
                      <div className="text-xs text-slate-500">Member Since</div>
                      <div className="font-medium text-slate-900">
                        {new Date(provider.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Reviews Section */}
        <div className="mt-8 bg-white rounded-xl border border-slate-200 overflow-hidden" data-testid="reviews-section">
          <div className="p-6 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h2 className="font-heading text-xl font-bold text-slate-900">Reviews</h2>
              <p className="text-sm text-slate-600 mt-1">
                {reviewStats.total_count} review{reviewStats.total_count !== 1 ? 's' : ''} • {reviewStats.average_rating.toFixed(1)} average
              </p>
            </div>
            {user && !userHasReviewed && (
              <Dialog open={reviewOpen} onOpenChange={setReviewOpen}>
                <DialogTrigger asChild>
                  <Button className="bg-blue-700 hover:bg-blue-800 rounded-full" data-testid="write-review-btn">
                    <Star className="h-4 w-4 mr-2" />
                    Write a Review
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Review {provider.business_name}</DialogTitle>
                  </DialogHeader>
                  <form onSubmit={handleSubmitReview} className="space-y-4 mt-4">
                    <div>
                      <Label className="mb-3 block">Your Rating</Label>
                      <StarRating
                        rating={reviewForm.rating}
                        onRatingChange={(rating) => setReviewForm(prev => ({ ...prev, rating }))}
                        interactive={true}
                      />
                    </div>
                    <div>
                      <Label htmlFor="review-comment">Your Review</Label>
                      <Textarea
                        id="review-comment"
                        required
                        rows={4}
                        placeholder="Share your experience working with this provider..."
                        value={reviewForm.comment}
                        onChange={(e) => setReviewForm(prev => ({ ...prev, comment: e.target.value }))}
                        className="mt-1"
                      />
                    </div>
                    <DialogFooter>
                      <Button type="submit" disabled={submitting} className="bg-blue-700 hover:bg-blue-800 rounded-full">
                        {submitting ? 'Submitting...' : 'Submit Review'}
                      </Button>
                    </DialogFooter>
                  </form>
                </DialogContent>
              </Dialog>
            )}
            {userHasReviewed && (
              <span className="text-sm text-green-600 font-medium">✓ You've reviewed this provider</span>
            )}
          </div>

          <div className="divide-y divide-slate-100">
            {reviews.length > 0 ? (
              reviews.map((review) => (
                <div key={review.review_id} className="p-6" data-testid={`review-${review.review_id}`}>
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="text-blue-700 font-medium">{review.user_name?.charAt(0) || 'A'}</span>
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-medium text-slate-900">{review.user_name || 'Anonymous'}</span>
                        <span className="text-sm text-slate-500">
                          {new Date(review.created_at).toLocaleDateString()}
                        </span>
                      </div>
                      <div className="mb-2">
                        <StarRating rating={review.rating} size="sm" />
                      </div>
                      <p className="text-slate-600">{review.comment}</p>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="p-8 text-center">
                <Star className="h-8 w-8 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600">No reviews yet</p>
                {user && !userHasReviewed && (
                  <p className="text-sm text-slate-500 mt-1">Be the first to leave a review!</p>
                )}
              </div>
            )}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-8 bg-blue-50 rounded-xl p-6 border border-blue-100 text-center">
          <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
            Looking for more providers?
          </h3>
          <p className="text-slate-600 mb-4">
            Browse our directory to find other investor-friendly professionals in your area.
          </p>
          <Button asChild variant="outline" className="rounded-full">
            <Link to="/directory">Browse Directory</Link>
          </Button>
        </div>
      </div>
      </AuthGate>

      <Footer />
    </div>
  );
}
